/**
 * /activa-peru — Landing de alta conversión para el mercado peruano
 * Plan 30 días Neuralia369 — Sep/Oct 2026
 */
import React, { useState } from 'react';
import { Check, ChevronDown, MessageCircle, PhoneCall, ArrowRight, Zap, Shield, Clock, Star, Users } from 'lucide-react';
import SEO from '../components/SEO';

const WA_LINK  = 'https://wa.me/19392947333';
const WA_TEXTO = (producto) => `${WA_LINK}?text=${encodeURIComponent(`Hola, quiero activar Neuralia369 — ${producto}`)}`;
const API_URL  = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Paleta ───────────────────────────────────────────────────────────────────
const NAVY  = '#0a1a3f';
const CYAN  = '#22d3ee';
const GOLD  = '#f2b441';

// ── Paquetes ─────────────────────────────────────────────────────────────────
const PAQUETES = [
  {
    id: 'consulta',
    nombre: 'Consultoría Express',
    badge: null,
    precio_sol: '490',
    precio_periodo: '',
    precio_usd: '129',
    desc: 'Ideal si quieres entender qué puede automatizar Aynara en tu empresa antes de comprometerte.',
    items: [
      'Sesión de diagnóstico 60 min (voz o video)',
      'Plan de automatización personalizado',
      '3 tareas IA identificadas y priorizadas',
      'Acceso de prueba a la plataforma 7 días',
    ],
    cta: 'Consultar por WhatsApp',
    color: CYAN,
    pago: 'transferencia',
  },
  {
    id: 'diagnostico',
    nombre: 'Diagnóstico Neuralia',
    badge: 'MÁS VENDIDO',
    precio_sol: '2,490',
    precio_periodo: '',
    precio_usd: '649',
    desc: 'Tu empresa conectada a Aynara en menos de 7 días. Automatizaciones activas desde el primer lunes.',
    items: [
      'Todo lo de Consultoría Express',
      'Activación del panel Neuralia369',
      '5 automatizaciones configuradas a medida',
      'WhatsApp Business conectado',
      'Gmail + CRM integrados',
      '1 mes de soporte por WhatsApp',
      'Informe semanal automático',
    ],
    cta: 'Activar ahora',
    color: GOLD,
    pago: 'stripe_o_transferencia',
  },
  {
    id: 'crecimiento',
    nombre: 'Crecimiento 369',
    badge: 'SISTEMA COMPLETO',
    precio_sol: '3,990',
    precio_periodo: '/mes',
    precio_usd: '1,049',
    desc: 'Aynara trabaja como tu equipo de operaciones, ventas y contenido los 7 días — en piloto automático.',
    items: [
      'Todo lo de Diagnóstico Neuralia',
      '15 herramientas conectadas',
      'Agente de ventas IA activo 24/7',
      'Publicación automática en redes',
      'Agente de soporte al cliente 24/7',
      'Facturación y cobros automatizados',
      '3 meses de soporte prioritario',
      'Sesión estratégica mensual con Juan Carlos',
    ],
    cta: 'Quiero el sistema completo',
    color: '#a78bfa',
    pago: 'stripe_o_transferencia',
  },
];

const DOLOR_PUNTOS = [
  { icon: '⏰', titulo: '4 horas al día en WhatsApp', desc: 'Respondiendo lo mismo, a clientes que ya te preguntaron antes.' },
  { icon: '📋', titulo: 'Leads que se pierden solos', desc: 'No hay seguimiento. El cliente interesado del lunes ya olvidó que te escribió.' },
  { icon: '📊', titulo: 'No sabes qué está funcionando', desc: 'Sin datos reales no puedes decidir dónde poner tu energía.' },
  { icon: '😰', titulo: 'Todo depende de ti', desc: 'Si tú paras, la empresa para. No puedes escalar así.' },
];

const FAQS = [
  { q: '¿Funciona para empresas fuera de Lima?', a: 'Sí. Trabajamos con empresas en Arequipa, Trujillo, Cusco y en toda LATAM. Todo es remoto.' },
  { q: '¿Necesito saber de tecnología para usar Aynara?', a: 'No. Nosotros nos encargamos de la configuración completa. Tú solo usas WhatsApp y el panel simple.' },
  { q: '¿En cuánto tiempo empieza a funcionar?', a: 'En 7 días hábiles desde que firmas el contrato y hacemos el primer pago.' },
  { q: '¿Puedo pagar en soles o solo en dólares?', a: 'Puedes pagar en soles por transferencia BCP o Interbank. También aceptamos tarjeta de crédito internacional.' },
  { q: '¿Qué pasa si no me convence?', a: 'Si en los primeros 14 días no ves el valor, conversamos. Nuestro interés es que funcione, no cobrar y desaparecer.' },
  { q: '¿Tienen casos reales en Perú?', a: 'Sí. Tenemos clientes activos en clínicas, consultoras, distribuidoras MLM y agencias de servicios en Lima y provincias.' },
];

export default function ActivaPeruPage() {
  const [form, setForm]       = useState({ nombre: '', empresa: '', whatsapp: '', sector: '', mensaje: '' });
  const [sent, setSent]       = useState(false);
  const [busy, setBusy]       = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [bcpOpen, setBcpOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await fetch(`${API_URL}/peru/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'activa_peru_landing' }),
      });
      setSent(true);
    } catch { /* sin-op */ }
    finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-white text-slate-800">
      <SEO
        title="Activa Neuralia369 en tu empresa — Automatización IA para Perú"
        description="Aynara trabaja por tu empresa mientras tú duermes. Ventas, clientes, contenido y operaciones en piloto automático. Precio en soles. Respuesta en 5 min."
        path="/activa-peru"
      />

      {/* ── Barra superior ── */}
      <div className="w-full py-2 text-center text-xs font-semibold" style={{ background: GOLD }}>
        Precio especial de lanzamiento en Perú · Plazas limitadas para septiembre · &nbsp;
        <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="underline">Reserva la tuya por WhatsApp</a>
      </div>

      {/* ── Hero ── */}
      <section style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #0d2757 60%, #0a1a3f 100%)` }} className="relative overflow-hidden px-6 pb-20 pt-16 text-white">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 40%, #22d3ee 0%, transparent 50%), radial-gradient(circle at 80% 80%, #f2b441 0%, transparent 40%)' }} />
        <div className="relative mx-auto max-w-4xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-widest" style={{ borderColor: `${CYAN}50`, color: CYAN }}>
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> Para empresas en Perú
          </div>
          <h1 className="mt-4 text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            ¿Cuánto tiempo pierde<br />
            <span style={{ color: CYAN }}>tu empresa cada semana</span><br />
            en tareas repetitivas?
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-300">
            <strong className="text-white">Aynara</strong> es la IA de Neuralia369 que trabaja por tu equipo: responde clientes, hace seguimiento de ventas, publica contenido y genera informes —
            <strong className="text-white"> mientras tú te enfocas en crecer.</strong>
          </p>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <a
              href={WA_TEXTO('Plan Diagnóstico Neuralia')}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-bold shadow-lg transition hover:scale-105"
              style={{ background: '#25D366', color: 'white' }}
              data-testid="activa-peru-wa-hero"
            >
              <MessageCircle size={20} /> Hablar por WhatsApp · Respuesta en 5 min
            </a>
            <a href="#precios" className="inline-flex items-center gap-2 rounded-full border px-8 py-4 text-base font-bold transition hover:border-[#22d3ee] hover:text-[#22d3ee]" style={{ borderColor: 'rgba(255,255,255,0.3)' }}>
              Ver precios en soles <ChevronDown size={18} />
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
            {['Sin contrato mínimo', 'Configurado en 7 días', 'Soporte en WhatsApp', 'Paga en soles o dólares'].map((t) => (
              <span key={t} className="flex items-center gap-1.5"><Check size={14} className="text-emerald-400" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Dolor de la pyme peruana ── */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <h2 className="text-3xl font-black sm:text-4xl">¿Te suena familiar?</h2>
            <p className="mt-3 text-slate-500">Los 4 problemas que frenan a la mayoría de pymes en Lima (y en todo el Perú)</p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {DOLOR_PUNTOS.map((d) => (
              <div key={d.titulo} className="rounded-2xl border border-slate-200 p-6 transition hover:border-cyan-300 hover:shadow-md">
                <p className="text-3xl">{d.icon}</p>
                <p className="mt-3 text-base font-bold">{d.titulo}</p>
                <p className="mt-2 text-sm text-slate-500">{d.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 rounded-2xl p-6 text-center" style={{ background: `${NAVY}08`, border: `1px solid ${CYAN}30` }}>
            <p className="text-lg font-bold" style={{ color: NAVY }}>Si marcaste 2 o más — Aynara puede recortar ese tiempo a la mitad en menos de 30 días.</p>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white transition hover:opacity-90" style={{ background: NAVY }}>
              <MessageCircle size={16} /> Cuéntame el caso de tu empresa
            </a>
          </div>
        </div>
      </section>

      {/* ── Precios en soles ── */}
      <section id="precios" className="px-6 py-20" style={{ background: '#f8faff' }}>
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: CYAN }}>Precios en soles · Septiembre 2026</p>
            <h2 className="mt-3 text-3xl font-black sm:text-4xl">Elige tu punto de entrada</h2>
            <p className="mt-3 text-slate-500">Sin permanencia mínima. Sin sorpresas. Empiezas cuando quieras.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {PAQUETES.map((p) => (
              <div
                key={p.id}
                data-testid={`activa-peru-plan-${p.id}`}
                className={`relative overflow-hidden rounded-3xl border-2 p-8 transition hover:shadow-xl ${p.badge ? 'shadow-lg' : ''}`}
                style={{ borderColor: p.badge ? p.color : '#e2e8f0', background: p.badge ? `${NAVY}` : 'white' }}
              >
                {p.badge && (
                  <div className="absolute right-5 top-5 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest text-white" style={{ background: GOLD }}>
                    {p.badge}
                  </div>
                )}
                <p className={`text-sm font-bold ${p.badge ? 'text-slate-300' : 'text-slate-500'}`}>{p.nombre}</p>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className={`text-4xl font-black ${p.badge ? 'text-white' : 'text-slate-800'}`}>S/ {p.precio_sol}</span>
                  <span className={`text-sm ${p.badge ? 'text-slate-400' : 'text-slate-400'}`}>{p.precio_periodo} · USD {p.precio_usd}</span>
                </div>
                <p className={`mt-3 text-sm ${p.badge ? 'text-slate-300' : 'text-slate-500'}`}>{p.desc}</p>
                <ul className="mt-5 space-y-2.5">
                  {p.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <Check size={15} className="mt-0.5 shrink-0" style={{ color: p.color }} />
                      <span className={p.badge ? 'text-slate-200' : 'text-slate-700'}>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 space-y-2">
                  <a
                    href={WA_TEXTO(p.nombre)}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 rounded-full py-3 text-sm font-bold text-white transition hover:opacity-90"
                    style={{ background: p.color === GOLD ? GOLD : p.badge ? CYAN : NAVY }}
                    data-testid={`activa-peru-cta-${p.id}`}
                  >
                    <MessageCircle size={15} /> {p.cta}
                  </a>
                  <button
                    type="button"
                    onClick={() => setBcpOpen(true)}
                    className="flex w-full items-center justify-center gap-1.5 rounded-full border py-2.5 text-xs font-semibold transition hover:opacity-80"
                    style={{ borderColor: p.badge ? 'rgba(255,255,255,0.2)' : '#e2e8f0', color: p.badge ? 'rgba(255,255,255,0.6)' : '#64748b' }}
                  >
                    Pagar por transferencia BCP / Interbank
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-slate-400">*Precios al tipo de cambio del momento. Equivalente referencial en dólares. Pago en soles aceptado sin recargo.</p>
        </div>
      </section>

      {/* ── Cómo funciona ── */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <div className="text-center">
            <h2 className="text-3xl font-black sm:text-4xl">De tu primera llamada a Aynara trabajando</h2>
            <p className="mt-3 text-slate-500">4 pasos. 7 días. Sin tener que saber de tecnología.</p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: '01', titulo: 'Diagnóstico', desc: 'Llamada de 60 min para mapear qué automatizar primero.' },
              { n: '02', titulo: 'Firma + Pago', desc: 'Contrato digital en 5 min. Paga por Stripe o transferencia BCP.' },
              { n: '03', titulo: 'Configuración', desc: 'En 5 días hábiles conectamos tus herramientas a Aynara.' },
              { n: '04', titulo: 'Aynara activa', desc: 'Tu empresa en piloto automático. Informe el primer lunes.' },
            ].map((s) => (
              <div key={s.n} className="relative rounded-2xl border border-slate-200 p-6">
                <span className="text-4xl font-black" style={{ color: `${CYAN}40` }}>{s.n}</span>
                <p className="mt-2 text-base font-bold">{s.titulo}</p>
                <p className="mt-1 text-sm text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Formulario de contacto ── */}
      <section id="activar" className="px-6 py-20" style={{ background: '#f8faff' }}>
        <div className="mx-auto max-w-2xl">
          <div className="text-center">
            <h2 className="text-3xl font-black">Cuéntanos sobre tu empresa</h2>
            <p className="mt-3 text-slate-500">Te respondemos en menos de 5 minutos por WhatsApp en horario laboral.</p>
          </div>
          {sent ? (
            <div className="mt-8 rounded-2xl border border-emerald-300 bg-emerald-50 p-8 text-center">
              <p className="text-2xl">🎉</p>
              <p className="mt-2 text-lg font-bold text-emerald-700">¡Recibido! Te contactamos en breve.</p>
              <p className="mt-1 text-sm text-emerald-600">También puedes escribirnos directamente por WhatsApp para una respuesta inmediata.</p>
              <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white" style={{ background: '#25D366' }}>
                <MessageCircle size={16} /> Ir a WhatsApp
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-4 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="activa-peru-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Tu nombre</label>
                  <input required className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-400 focus:outline-none" placeholder="Juan Pérez" value={form.nombre} onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))} data-testid="activa-peru-form-nombre" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Empresa o negocio</label>
                  <input required className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-400 focus:outline-none" placeholder="Distribuidora Pérez SAC" value={form.empresa} onChange={(e) => setForm((p) => ({ ...p, empresa: e.target.value }))} data-testid="activa-peru-form-empresa" />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">WhatsApp</label>
                  <input required className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-400 focus:outline-none" placeholder="+51 999 999 999" value={form.whatsapp} onChange={(e) => setForm((p) => ({ ...p, whatsapp: e.target.value }))} data-testid="activa-peru-form-whatsapp" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Sector</label>
                  <select className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-400 focus:outline-none" value={form.sector} onChange={(e) => setForm((p) => ({ ...p, sector: e.target.value }))} data-testid="activa-peru-form-sector">
                    <option value="">Selecciona...</option>
                    <option>Consultoría / Servicios</option>
                    <option>Salud / Clínica</option>
                    <option>Retail / Distribución</option>
                    <option>Inmobiliaria</option>
                    <option>Educación</option>
                    <option>MLM / Venta directa</option>
                    <option>Restaurante / Gastronomía</option>
                    <option>Otro</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">¿Qué quieres automatizar? (opcional)</label>
                <textarea className="min-h-[80px] w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-cyan-400 focus:outline-none" placeholder="Ej: Seguimiento de clientes, respuestas en WhatsApp, informes semanales..." value={form.mensaje} onChange={(e) => setForm((p) => ({ ...p, mensaje: e.target.value }))} data-testid="activa-peru-form-mensaje" />
              </div>
              <button type="submit" disabled={busy} className="w-full rounded-full py-4 text-sm font-bold text-white transition hover:opacity-90 disabled:opacity-50" style={{ background: NAVY }} data-testid="activa-peru-form-submit">
                {busy ? 'Enviando...' : 'Quiero que Aynara trabaje para mi empresa →'}
              </button>
              <p className="text-center text-xs text-slate-400">O escríbenos directo: <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="font-semibold text-emerald-600">+1 (787) 298-1758 WhatsApp</a></p>
            </form>
          )}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-center text-3xl font-black">Preguntas frecuentes</h2>
          <div className="mt-8 space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 overflow-hidden" data-testid={`activa-peru-faq-${i}`}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-5 py-4 text-left text-sm font-semibold"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  {faq.q}
                  <ChevronDown size={16} className={`shrink-0 transition ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="border-t border-slate-100 bg-slate-50 px-5 py-4 text-sm text-slate-600">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Final ── */}
      <section className="px-6 py-20 text-white" style={{ background: NAVY }}>
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-black sm:text-4xl">¿Tu competencia ya tiene IA trabajando por ellos?</h2>
          <p className="mt-4 text-lg text-slate-300">Cada semana que esperas es una semana más de leads perdidos, horas desperdiciadas y distancia respecto a quien ya lo activó.</p>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <a href={WA_TEXTO('Quiero activar Neuralia369')} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-bold text-[#0a1a3f] transition hover:opacity-90" style={{ background: '#25D366' }} data-testid="activa-peru-wa-bottom">
              <MessageCircle size={20} /> WhatsApp: respuesta inmediata
            </a>
            <a href="#activar" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-8 py-4 text-base font-bold transition hover:border-white">
              Llenar formulario <ArrowRight size={18} />
            </a>
          </div>
          <p className="mt-6 text-sm text-slate-400">Número directo: <strong className="text-white">+1 (787) 298-1758</strong> · Lima, Perú · Lunes a sábado 8am – 8pm</p>
        </div>
      </section>

      {/* ── Modal BCP ── */}
      {bcpOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 sm:items-center" data-testid="activa-peru-bcp-modal">
          <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-7">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-black">Transferencia BCP / Interbank</h3>
              <button type="button" onClick={() => setBcpOpen(false)} className="rounded-full border p-2 text-slate-400 hover:text-slate-700" aria-label="Cerrar">✕</button>
            </div>
            <div className="mt-4 space-y-3 text-sm">
              <div className="rounded-xl border bg-slate-50 p-4">
                <p className="font-bold text-slate-800">Banco de Crédito del Perú (BCP)</p>
                <p className="mt-1 text-slate-600">Cuenta Corriente Soles: <strong>XXXX-XXXXXXXXX-X-XX</strong></p>
                <p className="text-slate-600">CCI: <strong>002-XXXXXXXXXXXXXXXXXX</strong></p>
                <p className="text-slate-600">A nombre de: <strong>Neuralia369 / Juan Carlos Vargas</strong></p>
              </div>
              <div className="rounded-xl border bg-slate-50 p-4">
                <p className="font-bold text-slate-800">Interbank</p>
                <p className="mt-1 text-slate-600">Cuenta Ahorros Soles: <strong>XXXXXXXXXX</strong></p>
                <p className="text-slate-600">CCI: <strong>003-XXXXXXXXXXXXXXXXXX</strong></p>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="font-semibold text-amber-800">Una vez realizada la transferencia:</p>
                <p className="mt-1 text-amber-700">Envía el voucher por WhatsApp al <strong>+1 (787) 298-1758</strong> y en menos de 2 horas activamos tu cuenta.</p>
              </div>
            </div>
            <a href={WA_TEXTO('Voucher de transferencia')} target="_blank" rel="noopener noreferrer" className="mt-4 flex items-center justify-center gap-2 rounded-full py-3 text-sm font-bold text-white" style={{ background: '#25D366' }}>
              <MessageCircle size={16} /> Enviar voucher por WhatsApp
            </a>
          </div>
        </div>
      )}

      {/* ── WhatsApp flotante ── */}
      <a
        href={WA_LINK}
        target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full shadow-2xl transition hover:scale-110"
        style={{ background: '#25D366' }}
        data-testid="activa-peru-wa-float"
        aria-label="WhatsApp Neuralia369"
      >
        <MessageCircle size={28} className="text-white" />
      </a>
    </main>
  );
}
